/**
 * Video-conference link detection for calendar events.
 *
 * External (Google/Outlook) events usually carry an explicit `conferenceUrl`.
 * Internal Scrambl events don't — organizers paste the join link into the
 * location or description, so we sniff it out of that free text.
 */

const CONFERENCE_HOSTS: { pattern: RegExp; provider: string }[] = [
  { pattern: /meet\.google\.com/i, provider: 'Google Meet' },
  { pattern: /zoom\.us|zoom\.com/i, provider: 'Zoom' },
  { pattern: /teams\.microsoft\.com|teams\.live\.com/i, provider: 'Teams' },
  { pattern: /webex\.com/i, provider: 'Webex' },
  { pattern: /whereby\.com/i, provider: 'Whereby' },
  { pattern: /meet\.jit\.si/i, provider: 'Jitsi' },
  { pattern: /gotomeeting\.com/i, provider: 'GoToMeeting' },
  { pattern: /chime\.aws/i, provider: 'Chime' },
  { pattern: /bluejeans\.com/i, provider: 'BlueJeans' },
  { pattern: /slack\.com\/(?:call|huddle)/i, provider: 'Slack' },
]

const URL_PATTERN = /https?:\/\/[^\s<>"')]+/gi

/**
 * Calendar providers wrap links in a tracking redirect
 * (`https://www.google.com/url?q=<real>&sa=…`). Unwrap to the real target and
 * decode HTML/URL entities so the join link is clean and directly openable.
 */
function normalizeUrl(rawUrl: string): string {
  let url = rawUrl.replace(/[.,;:]+$/, '') // trailing prose punctuation
  // HTML entities that survive when a URL is pulled from an href attribute.
  url = url.replace(/&amp;/gi, '&')
  const redirect = url.match(/[?&](?:q|url)=(https?[^&]+)/i)
  if (redirect) {
    try {
      url = decodeURIComponent(redirect[1])
    } catch {
      url = redirect[1]
    }
  }
  return url
}

/** Human label for a raw provider string or join URL. */
export function getConferenceProviderLabel(
  provider: string | undefined,
  url: string | undefined,
): string {
  const known = url && CONFERENCE_HOSTS.find(host => host.pattern.test(url))
  if (known) return known.provider
  if (!provider) return 'Join'
  // Normalize provider slugs like 'google_meet' / 'hangoutsMeet'
  const normalized = provider.replace(/[_-]+/g, ' ').trim()
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

/**
 * Find a video-call join URL in free text (event location/description).
 * Returns the first URL pointing at a known conferencing host.
 */
export function findConferenceUrl(
  ...sources: (string | null | undefined)[]
): string | undefined {
  for (const source of sources) {
    if (!source) continue
    const urls = source.match(URL_PATTERN)
    if (!urls) continue
    for (const rawUrl of urls) {
      const url = normalizeUrl(rawUrl)
      if (CONFERENCE_HOSTS.some(host => host.pattern.test(url))) return url
    }
  }
  return undefined
}
